import { useCallback } from 'react';
import { getRangeForEvent, isNil, isNotNil } from 'shared/src/utils';
import { useAddLikeGoods, useLikeMap } from 'shared/src/hooks';
import { useRemoveLikeGoods } from '../../../queries/useRemoveLikeGoods';
import { bridge } from 'shared/src/utils';
import { useGoodsSno } from '../../../hooks/useGoodsSno';
import { usePostGoodsActionBottomSheet } from '../../PostGoodsActionBottomSheet';
import { useGoodsBasic } from '../../../queries/useGoodsBasic';
import { useLikeActionAnimation } from '../components/LikeActionAnimation';
import { useLogGoodsAnalyticsEvent } from '../../../hooks/useLogGoodsAnalyticsEvent';
import { useGoodsDetail } from '../../../queries/useGoodsDetail';
import { LOG_ANALYTICS_ALL_CHANNELS } from 'shared/src/utils/bridge';
import { useGoodsLikeRecommend } from '../queries/useGoodsLikeRecommend';

export const useOnPressLike = ({
  initialIsLiked,
}: {
  initialIsLiked: boolean;
}) => {
  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDetail } = useGoodsDetail();
  const sno = useGoodsSno();
  const { data: recommendGoods } = useGoodsLikeRecommend();

  const { likeMap, setLikeMap } = useLikeMap({
    initialLikeMap: { [sno]: initialIsLiked },
  });
  const { show: showPostGoodsActionBottomSheet } =
    usePostGoodsActionBottomSheet();
  const { addLikeGoods } = useAddLikeGoods({
    hideToast: (recommendGoods?.carousels ?? []).length > 0,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: 'GOODS_DETAIL',
  });
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { show: showLikeActionAnimation } = useLikeActionAnimation();
  const logGoodsAnalytics = useLogGoodsAnalyticsEvent();

  const isLiked = likeMap[sno] ?? initialIsLiked;

  const handlePress = useCallback(async () => {
    if (isNil(goodsBasic) || isNil(goodsDetail) || isNil(recommendGoods)) {
      return;
    }
    if (!isLiked) {
      logGoodsAnalytics({
        name: 'CLICK_LIKE_GOODS',
        channel: LOG_ANALYTICS_ALL_CHANNELS,
        analytics: {
          LIKES_COUNT: goodsDetail.goods.score.likes_count,
          LIKES_COUNT_RANGE: getRangeForEvent({
            target: goodsDetail.goods.score.likes_count,
            rangeUnit: 500,
          }),
        },
      });
      const result = await addLikeGoods(sno, {
        SCREEN: 'GOODS_DETAIL',
      });
      if (isNotNil(result)) {
        await showLikeActionAnimation();
        if (recommendGoods.carousels.length === 0) return;

        await showPostGoodsActionBottomSheet({
          mode: 'like',
          title: {
            highlight: result.folderName,
            text: '에 상품을 찜했어요',
          },
          image: goodsBasic.cover_images[0],
          action: {
            text: '바로가기',
            deeplink: `ably://like/folders/${result.folderSno}`,
          },
          folderSno: result.folderSno,
        });
      }
    } else {
      logGoodsAnalytics({
        name: 'CLICK_UNLIKE_GOODS',
        channel: ['firebase', 'facebook', 'amplitude', 'braze', 'ablylog'],
        analytics: {
          LIKES_COUNT: goodsDetail.goods.score.likes_count,
          LIKES_COUNT_RANGE: getRangeForEvent({
            target: goodsDetail.goods.score.likes_count,
            rangeUnit: 500,
          }),
        },
      });
      await mutateRemoveLikeGoods({ sno });
      bridge.likeGoods({
        goods_sno: sno,
        is_liked: false,
      });
      setLikeMap(prevState => ({ ...prevState, [sno]: false }));
    }
  }, [
    goodsBasic,
    goodsDetail,
    recommendGoods,
    isLiked,
    logGoodsAnalytics,
    addLikeGoods,
    sno,
    showLikeActionAnimation,
    showPostGoodsActionBottomSheet,
    mutateRemoveLikeGoods,
    setLikeMap,
  ]);

  return { handlePress, isLiked };
};
